import React, { memo, useCallback, useState } from 'react';
import { Button, Col, Form, Row } from 'antd';
import { DragSourceMonitor, useDrag, useDrop } from 'react-dnd';
import { Identifier, XYCoord } from 'dnd-core';
import { DraggableFormItem } from './index';
import { DeleteTwoTone, EditTwoTone, UploadOutlined } from '@ant-design/icons';
import styles from './styles.module.pcss';
import { If } from '@/components';

type Props = {
  formItem: DraggableFormItem;
  onChangeOrder: (dragIndex: number, hoverIndex: number) => void;
  onDeleteFormItem: (id: string) => void;
  index: number;
  toggleEditDrawer: (open: boolean, id: string) => void;
};

const FormItem: React.FC<Props> = memo(({ formItem, index, onChangeOrder, onDeleteFormItem, toggleEditDrawer }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [showIcon, setIconVisibility] = useState(false);

  const deleteFormItem = () => {
    onDeleteFormItem(formItem.id);
  };

  const [{ handlerId }, drop] = useDrop<DraggableFormItem, void, { handlerId: Identifier | null }>({
    accept: 'FORMITEM',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      onChangeOrder(dragIndex, hoverIndex);

      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: 'FORMITEM',
    item: () => {
      return { id: formItem.id, index };
    },
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  const renderFormItemComponent = useCallback(() => {
    const { component: Component } = formItem;

    let FormItemComponent: JSX.Element;

    switch (formItem.type) {
      case 'button':
        FormItemComponent = <Component type="primary">Button</Component>;
        break;
      case 'select':
      case 'checkboxes':
      case 'radio':
        FormItemComponent = <Component type="primary" placeholder={formItem.placeholder} options={formItem.options} />;
        break;
      case 'text':
        if (formItem.label === 'Header Text') {
          FormItemComponent = <Component level={1}>{formItem.label}</Component>;
        } else {
          FormItemComponent = <Component>{formItem.label}</Component>;
        }
        break;
      case 'upload':
        FormItemComponent = (
          <Component type="primary">
            <Button icon={<UploadOutlined />}>Click to Upload</Button>
          </Component>
        );
        break;
      default:
        FormItemComponent = (
          <Component
            placeholder={formItem.placeholder}
            style={{ color: formItem.color, borderColor: formItem.color }}
          />
        );
    }

    const label = formItem.name.startsWith('Arya') ? formItem.label : `${formItem.label}: ${formItem.name}`;

    return (
      <Form.Item label={label} name={formItem.name} required={formItem.required}>
        {FormItemComponent}
      </Form.Item>
    );
  }, [formItem]);

  const toggleIconsVisibility = useCallback((isVisible: boolean) => {
    setIconVisibility(isVisible);
  }, []);

  return (
    <Row
      ref={ref}
      style={{ opacity: isDragging ? 0.4 : 1 }}
      gutter={[16, 8]}
      data-handler-id={handlerId}
      className={styles.formItem}
      onMouseOver={() => toggleIconsVisibility(true)}
      onMouseLeave={() => toggleIconsVisibility(false)}
    >
      <Col span={22}>{renderFormItemComponent()}</Col>
      <Col span={1}>
        <If condition={showIcon}>
          <EditTwoTone onClick={() => toggleEditDrawer(true, formItem.id)} style={{ fontSize: '1.3rem' }} />
        </If>
      </Col>
      <Col span={1}>
        <If condition={showIcon}>
          <DeleteTwoTone onClick={deleteFormItem} style={{ fontSize: '1.3rem' }} />
        </If>
      </Col>
    </Row>
  );
});

export default FormItem;
